import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { FaCcStripe } from "react-icons/fa";
import {
  MdAutorenew,
  MdContentCopy,
  MdDelete,
  MdDownload,
  MdPersonSearch,
  MdVisibility,
  MdVisibilityOff,
  MdVpnKey,
} from "react-icons/md";
import { TiArrowSortedDown, TiArrowSortedUp, TiArrowUnsorted } from "react-icons/ti";
import { fetchController } from "../../utils/FetchController/fetchController";
import Paginator from "../../utils/Loader/Paginator";
import PaginationSizeSelect from "../../utils/Loader/PaginationSizeSelect";
import { getHostDomain } from "../../utils/utils";

// ─── Sortable header ──────────────────────────────────────────────────────────

function SortTh({ label, sortKey, sortBy, sortDir, onSort }) {
  const active = sortBy === sortKey;
  return (
    <Th
      cursor="pointer"
      userSelect="none"
      onClick={() => onSort(sortKey)}
      whiteSpace="nowrap"
    >
      <Flex align="center" gap={1}>
        {label}
        {active
          ? sortDir === "desc"
            ? <TiArrowSortedDown />
            : <TiArrowSortedUp />
          : <TiArrowUnsorted opacity={0.3} />}
      </Flex>
    </Th>
  );
}

// ─── Stripe paid cell ─────────────────────────────────────────────────────────

function StripePaidCell({ userId, baseUrl }) {
  const [data, setData] = useState(null);   // {total_paid, currency, invoice_count}
  const [loading, setLoading] = useState(false);

  async function fetch() {
    if (data || loading) return;
    setLoading(true);
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/insighto-admin/users/${userId}/stripe_paid`,
        "GET"
      );
      setData(res?.data);
    } catch {
      setData({ total_paid: null });
    } finally {
      setLoading(false);
    }
  }

  if (data) {
    return (
      <Tooltip label={`${data.invoice_count} paid invoice(s)`}>
        <Text fontSize="sm" color={data.total_paid > 0 ? "green.600" : "gray.400"} fontWeight="semibold">
          {data.total_paid != null ? `$${data.total_paid}` : "—"}
        </Text>
      </Tooltip>
    );
  }

  return (
    <Tooltip label="Fetch Stripe total paid">
      <Button size="xs" variant="ghost" colorScheme="purple" onClick={fetch} isLoading={loading}>
        <FaCcStripe size={18} />
      </Button>
    </Tooltip>
  );
}

// ─── Wallet adjust cell ───────────────────────────────────────────────────────

function WalletCell({ user, baseUrl, onUpdated }) {
  const [amount, setAmount] = useState(0);
  const [saving, setSaving] = useState(false);

  async function handleAdjust(onClose) {
    if (!amount || amount === 0) return;
    setSaving(true);
    try {
      await fetchController(
        `${baseUrl}/api/v1/insighto-admin/users/${user.id}/wallet`,
        "POST",
        { amount: parseFloat(amount) }
      );
      onUpdated();
      onClose();
    } catch (e) {
      alert("Failed: " + (e?.message || "error"));
    } finally {
      setSaving(false);
    }
  }

  const balance = user.wallet_balance;

  return (
    <Flex align="center" gap={2}>
      <Text fontSize="sm" minW="52px">
        {balance != null ? `$${balance.toFixed(2)}` : "—"}
      </Text>
      <Popover onOpen={() => setAmount(0)}>
        {({ onClose }) => (
          <>
            <PopoverTrigger>
              <Button size="xs" variant="outline" colorScheme="teal">Adjust</Button>
            </PopoverTrigger>
            <PopoverContent w="220px">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader fontSize="sm">Adjust wallet</PopoverHeader>
              <PopoverBody>
                <Text fontSize="xs" color="gray.500" mb={2}>Positive = credit, negative = deduct</Text>
                <NumberInput value={amount} onChange={(_, v) => setAmount(v)} step={1} precision={2} size="sm">
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Button mt={2} size="sm" colorScheme="teal" w="100%" isLoading={saving} onClick={() => handleAdjust(onClose)}>
                  Apply
                </Button>
              </PopoverBody>
            </PopoverContent>
          </>
        )}
      </Popover>
    </Flex>
  );
}

// ─── Billing plan inline select ───────────────────────────────────────────────

function BillingPlanCell({ user, baseUrl, billingPlanOptions, onUpdated }) {
  const [saving, setSaving] = useState(false);

  async function handleChange(planId) {
    if (!planId || planId === user.billing_plan_id) return;
    setSaving(true);
    try {
      await fetchController(
        `${baseUrl}/api/v1/insighto-admin/users/${user.id}/billing_plan`,
        "POST",
        { billing_plan_id: planId }
      );
      onUpdated();
    } catch (e) {
      alert("Failed: " + (e?.message || "error"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Flex align="center" gap={2}>
      <Select size="xs" value={user.billing_plan_id || ""} onChange={(e) => handleChange(e.target.value)} maxW="160px" isDisabled={saving}>
        <option value="">— none —</option>
        {billingPlanOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </Select>
      {saving && <Spinner size="xs" />}
    </Flex>
  );
}

// ─── Users Tab ────────────────────────────────────────────────────────────────

function UsersTab() {
  const baseUrl = getHostDomain();

  const [status, setStatus] = useState("idle");
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [paginationSize, setPaginationSize] = useState(50);

  const [emailInput, setEmailInput] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [billingPlanFilter, setBillingPlanFilter] = useState("");
  const [billingPlanOptions, setBillingPlanOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [exporting, setExporting] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmInput, setConfirmInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  const {
    isOpen: isPwdOpen,
    onOpen: onPwdOpen,
    onClose: onPwdClose,
  } = useDisclosure();
  const [pwdTarget, setPwdTarget] = useState(null);
  const [pwdValue, setPwdValue] = useState("");
  const [pwdShow, setPwdShow] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdSavedValue, setPwdSavedValue] = useState("");

  // Debounce email → reset page
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setEmailFilter(emailInput); }, 400);
    return () => clearTimeout(t);
  }, [emailInput]);

  // Reset page on filter/size/sort changes
  useEffect(() => { setPage(1); }, [billingPlanFilter, statusFilter, paginationSize, sortBy, sortDir]);

  // Load billing plans once
  useEffect(() => {
    fetchController(`${baseUrl}/api/v1/insighto-admin/billing_plans`, "GET")
      .then((res) => setBillingPlanOptions(res?.data || []))
      .catch(() => {});
  }, [baseUrl]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, paginationSize, emailFilter, billingPlanFilter, statusFilter, sortBy, sortDir]);

  async function fetchUsers() {
    setStatus("loading");
    try {
      const params = new URLSearchParams({ page, size: paginationSize, sort_by: sortBy, sort_dir: sortDir });
      if (emailFilter) params.set("email", emailFilter);
      if (billingPlanFilter) params.set("billing_plan_id", billingPlanFilter);
      if (statusFilter !== "") params.set("is_active", statusFilter);
      const res = await fetchController(`${baseUrl}/api/v1/insighto-admin/users?${params}`, "GET");
      const { items = [], pages: p = 1, total: t = 0 } = res?.data || {};
      setUsers(items);
      setPages(p);
      setTotal(t);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  function handleSort(key) {
    if (sortBy === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  }

  async function handleExport(format) {
    setExporting(true);
    try {
      const params = new URLSearchParams({ sort_by: sortBy, sort_dir: sortDir });
      if (emailFilter) params.set("email", emailFilter);
      if (billingPlanFilter) params.set("billing_plan_id", billingPlanFilter);
      if (statusFilter !== "") params.set("is_active", statusFilter);
      const res = await fetchController(`${baseUrl}/api/v1/insighto-admin/users/export?${params}`, "GET");
      const allUsers = res?.data || [];

      const rows = allUsers.map((u) => ({
        Status: u.is_active ? "Active" : "Inactive",
        "First Name": u.first_name || "",
        "Last Name": u.last_name || "",
        Email: u.email || "",
        Created: u.created_at ? new Date(u.created_at).toLocaleDateString() : "",
        "Last Login": u.last_login ? new Date(u.last_login).toLocaleString() : "Never",
        "Last Conversation": u.last_conversation ? new Date(u.last_conversation).toLocaleString() : "Never",
        "Billing Plan": u.billing_plan_name || "",
        "Wallet Balance": u.wallet_balance ?? "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Users");

      const filename = `insighto_users_${new Date().toISOString().slice(0, 10)}`;
      if (format === "csv") {
        XLSX.writeFile(wb, `${filename}.csv`, { bookType: "csv" });
      } else {
        XLSX.writeFile(wb, `${filename}.xlsx`);
      }
    } catch (e) {
      alert("Export failed: " + (e?.message || "unknown error"));
    } finally {
      setExporting(false);
    }
  }

  async function handleImpersonate(userId, email) {
    try {
      const res = await fetchController(`${baseUrl}/api/v1/insighto-admin/impersonate/${userId}`, "POST");
      const { access_token, refresh_token } = res?.data || {};
      if (!access_token) throw new Error("No token returned");
      localStorage.setItem("admin_token", localStorage.getItem("token"));
      localStorage.setItem("admin_refresh_token", localStorage.getItem("refresh_token") || "");
      localStorage.setItem("impersonated_email", email);
      localStorage.setItem("token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      window.location.href = "/dashboard";
    } catch (e) {
      alert("Impersonation failed: " + (e?.message || "unknown error"));
    }
  }

  function openDeleteModal(user) {
    setDeleteTarget(user);
    setConfirmInput("");
    onOpen();
  }

  async function handleDelete() {
    if (confirmInput !== deleteTarget.email) return;
    setDeleting(true);
    try {
      await fetchController(`${baseUrl}/api/v1/insighto-admin/users/${deleteTarget.id}`, "DELETE");
      onClose();
      fetchUsers();
    } catch (e) {
      alert("Delete failed: " + (e?.message || "unknown error"));
    } finally {
      setDeleting(false);
    }
  }

  function openPasswordModal(user) {
    setPwdTarget(user);
    setPwdValue("");
    setPwdShow(false);
    setPwdSavedValue("");
    onPwdOpen();
  }

  function generatePassword() {
    // 16 chars, mixed case + digits + a safe symbol set; uses crypto for entropy.
    const alphabet =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*";
    const bytes = new Uint32Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    let out = "";
    for (let i = 0; i < bytes.length; i++) {
      out += alphabet[bytes[i] % alphabet.length];
    }
    setPwdValue(out);
    setPwdShow(true);
  }

  async function handleSetPassword() {
    if (!pwdTarget || pwdValue.length < 8) return;
    setPwdSaving(true);
    try {
      await fetchController(
        `${baseUrl}/api/v1/insighto-admin/users/${pwdTarget.id}/password`,
        "POST",
        { new_password: pwdValue }
      );
      setPwdSavedValue(pwdValue);
      setPwdValue("");
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message;
      alert("Failed to set password: " + (detail || "unknown error"));
    } finally {
      setPwdSaving(false);
    }
  }

  const sortProps = { sortBy, sortDir, onSort: handleSort };

  return (
    <VStack align="stretch" spacing={4}>
      <Flex gap={3} wrap="wrap" align="center">
        <Input placeholder="Search by email..." value={emailInput} onChange={(e) => setEmailInput(e.target.value)} maxW="300px" size="sm" />
        <Select placeholder="All billing plans" value={billingPlanFilter} onChange={(e) => { setPage(1); setBillingPlanFilter(e.target.value); }} maxW="220px" size="sm">
          {billingPlanOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select placeholder="All statuses" value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }} maxW="160px" size="sm">
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </Select>
        <PaginationSizeSelect paginationSize={paginationSize} setPaginationSize={setPaginationSize} />
        <Flex ml="auto" align="center" gap={2}>
          <Text fontSize="sm" color="gray.500">{total} users</Text>
          <Tooltip label="Export CSV (all filtered results)">
            <Button size="sm" leftIcon={<MdDownload />} variant="outline" isLoading={exporting} onClick={() => handleExport("csv")}>
              CSV
            </Button>
          </Tooltip>
          <Tooltip label="Export Excel (all filtered results)">
            <Button size="sm" leftIcon={<MdDownload />} variant="outline" isLoading={exporting} onClick={() => handleExport("xlsx")}>
              Excel
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      {status === "loading" ? (
        <Flex justify="center" py={8}><Spinner /></Flex>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <SortTh label="Active" sortKey="is_active" {...sortProps} />
                <SortTh label="Name" sortKey="first_name" {...sortProps} />
                <SortTh label="Email" sortKey="email" {...sortProps} />
                <SortTh label="Created" sortKey="created_at" {...sortProps} />
                <SortTh label="Last Login" sortKey="last_login" {...sortProps} />
                <SortTh label="Last Conv." sortKey="last_conversation" {...sortProps} />
                <SortTh label="Billing Plan" sortKey="billing_plan_name" {...sortProps} />
                <SortTh label="Wallet" sortKey="wallet_balance" {...sortProps} />
                <Th>Stripe Paid</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.length === 0 ? (
                <Tr><Td colSpan={10} textAlign="center" py={6} color="gray.400">No users found</Td></Tr>
              ) : users.map((u) => (
                <Tr key={u.id}>
                  <Td><Badge colorScheme={u.is_active ? "green" : "red"}>{u.is_active ? "Active" : "Inactive"}</Badge></Td>
                  <Td>{`${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}</Td>
                  <Td>{u.email}</Td>
                  <Td whiteSpace="nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</Td>
                  <Td whiteSpace="nowrap">{u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}</Td>
                  <Td whiteSpace="nowrap">{u.last_conversation ? new Date(u.last_conversation).toLocaleString() : "Never"}</Td>
                  <Td>
                    <BillingPlanCell user={u} baseUrl={baseUrl} billingPlanOptions={billingPlanOptions} onUpdated={fetchUsers} />
                  </Td>
                  <Td>
                    <WalletCell user={u} baseUrl={baseUrl} onUpdated={fetchUsers} />
                  </Td>
                  <Td>
                    <StripePaidCell userId={u.id} baseUrl={baseUrl} />
                  </Td>
                  <Td>
                    <Flex gap={2}>
                      <Tooltip label={`Impersonate ${u.email}`}>
                        <IconButton size="xs" colorScheme="orange" icon={<MdPersonSearch size={14} />} aria-label="Impersonate" onClick={() => handleImpersonate(u.id, u.email)} />
                      </Tooltip>
                      <Tooltip label="Set password">
                        <IconButton size="xs" colorScheme="blue" variant="outline" icon={<MdVpnKey size={14} />} aria-label="Set password" onClick={() => openPasswordModal(u)} />
                      </Tooltip>
                      <Tooltip label="Delete user">
                        <IconButton size="xs" colorScheme="red" variant="outline" icon={<MdDelete size={14} />} aria-label="Delete" onClick={() => openDeleteModal(u)} />
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Flex align="center" justify="center" gap={3}>
        <Paginator page={page} pages={pages} onChangePage={setPage} />
        <Flex align="center" gap={2}>
          <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">Go to page</Text>
          <NumberInput
            size="sm"
            min={1}
            max={pages}
            w="70px"
            value=""
            onChange={(_, val) => {
              if (val >= 1 && val <= pages) setPage(val);
            }}
          >
            <NumberInputField placeholder={String(page)} />
          </NumberInput>
          <Text fontSize="sm" color="gray.400">/ {pages}</Text>
        </Flex>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete user</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3}>This will permanently delete <strong>{deleteTarget?.email}</strong> and all their data. This cannot be undone.</Text>
            <Text mb={2} fontSize="sm">Type the email address to confirm:</Text>
            <Input placeholder={deleteTarget?.email} value={confirmInput} onChange={(e) => setConfirmInput(e.target.value)} autoFocus />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button colorScheme="red" isDisabled={confirmInput !== deleteTarget?.email} isLoading={deleting} onClick={handleDelete}>
              Delete permanently
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPwdOpen} onClose={onPwdClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Set password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {pwdSavedValue ? (
              <>
                <Text mb={3} fontSize="sm">
                  Password updated for <strong>{pwdTarget?.email}</strong>. Copy
                  it now — it won't be shown again.
                </Text>
                <Flex
                  align="center"
                  gap={2}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={2}
                >
                  <Box as="code" flex="1" fontFamily="mono" fontSize="sm" wordBreak="break-all">
                    {pwdSavedValue}
                  </Box>
                  <Tooltip label="Copy">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      aria-label="Copy"
                      icon={<MdContentCopy size={14} />}
                      onClick={() => navigator.clipboard?.writeText(pwdSavedValue)}
                    />
                  </Tooltip>
                </Flex>
              </>
            ) : (
              <>
                <Text mb={3} fontSize="sm">
                  Set a new password for <strong>{pwdTarget?.email}</strong>.
                  The user is not notified — communicate the password to them
                  yourself. Minimum 8 characters.
                </Text>
                <Flex gap={2} align="center">
                  <Input
                    type={pwdShow ? "text" : "password"}
                    value={pwdValue}
                    onChange={(e) => setPwdValue(e.target.value)}
                    placeholder="New password"
                    fontFamily={pwdShow ? "mono" : undefined}
                    autoFocus
                  />
                  <Tooltip label={pwdShow ? "Hide" : "Show"}>
                    <IconButton
                      aria-label={pwdShow ? "Hide" : "Show"}
                      icon={pwdShow ? <MdVisibilityOff /> : <MdVisibility />}
                      variant="ghost"
                      onClick={() => setPwdShow((s) => !s)}
                    />
                  </Tooltip>
                  <Tooltip label="Generate strong password">
                    <IconButton
                      aria-label="Generate"
                      icon={<MdAutorenew />}
                      variant="outline"
                      onClick={generatePassword}
                    />
                  </Tooltip>
                </Flex>
                {pwdValue && pwdValue.length < 8 && (
                  <Text fontSize="xs" color="red.500" mt={2}>
                    Password must be at least 8 characters.
                  </Text>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            {pwdSavedValue ? (
              <Button colorScheme="blue" onClick={onPwdClose}>Done</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={onPwdClose}>Cancel</Button>
                <Button
                  colorScheme="blue"
                  isDisabled={pwdValue.length < 8}
                  isLoading={pwdSaving}
                  onClick={handleSetPassword}
                >
                  Set password
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

// ─── Billing Plans Tab ────────────────────────────────────────────────────────

function BillingPlansTab() {
  const baseUrl = getHostDomain();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    setStatus("loading");
    fetchController(`${baseUrl}/api/v1/insighto-admin/billing_plans`, "GET")
      .then((res) => { setPlans(res?.data || []); setStatus("success"); })
      .catch(() => setStatus("error"));
  }, [baseUrl]);

  return (
    <Box overflowX="auto">
      {status === "loading" ? (
        <Flex justify="center" py={8}><Spinner /></Flex>
      ) : (
        <Table size="sm" variant="simple">
          <Thead><Tr><Th>Plan Name</Th><Th isNumeric>Users</Th></Tr></Thead>
          <Tbody>
            {plans.length === 0
              ? <Tr><Td colSpan={2} textAlign="center" py={6} color="gray.400">No plans found</Td></Tr>
              : plans.map((p) => (
                <Tr key={p.id}>
                  <Td>{p.name}</Td>
                  <Td isNumeric>{p.user_count}</Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function InsightoAdminUsers() {
  return (
    <VStack h="100%" spacing={4} align="stretch">
      <Heading size="md">Insighto Admin</Heading>
      <Tabs isLazy>
        <TabList>
          <Tab>Users</Tab>
          <Tab>Billing Plans</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}><UsersTab /></TabPanel>
          <TabPanel px={0}><BillingPlansTab /></TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
